import type { CalendarEvent } from "@/hooks/useCalendarEvents";

/** Format a Date as YYYY-MM-DD */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Get the 7 dates of the week containing `d` (Sunday–Saturday) */
export function getWeekDates(d: Date): Date[] {
  const start = new Date(d);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start);
    day.setDate(day.getDate() + i);
    return day;
  });
}

/** Parse "HH:MM" or "HH:MM:SS" to minutes since midnight */
export function timeToMinutes(timeStr: string): number {
  const parts = timeStr.split(":");
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1] || "0", 10);
}

/** Minutes since midnight → "h:mm AM/PM" */
export function minutesToLabel(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/** Snap minutes to the nearest interval (default 15) */
export function snapToInterval(minutes: number, interval = 15): number {
  return Math.round(minutes / interval) * interval;
}

// ── Collision detection + layout for timed events ───────────────

export interface LayoutedEvent {
  event: CalendarEvent;
  top: number;       // pixels from top of time grid
  height: number;    // pixels
  leftPercent: number;
  widthPercent: number;
  startMinutes: number;
  endMinutes: number;
}

/**
 * Given events for a single day, compute pixel positions.
 * Events whose time ranges overlap are rendered side-by-side.
 *
 * @param events - timed events for one day (must have start_time)
 * @param hourHeight - pixels per hour (default 60)
 */
export function layoutTimedEvents(
  events: CalendarEvent[],
  hourHeight = 60,
): LayoutedEvent[] {
  if (events.length === 0) return [];

  const pxPerMinute = hourHeight / 60;
  const MIN_HEIGHT = 20; // minimum event block height in pixels

  // Calculate start/end minutes for each event
  const items = events
    .filter((e) => e.start_time)
    .map((e) => {
      const startMin = timeToMinutes(e.start_time!);
      const endMin = e.end_time ? timeToMinutes(e.end_time) : startMin + 60;
      return {
        event: e,
        startMinutes: startMin,
        endMinutes: Math.max(endMin, startMin + 15), // at least 15 min
      };
    })
    .sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);

  // Assign columns using greedy algorithm
  // Each column tracks the end time of its last event
  const columns: number[] = []; // column[i] = endMinutes of last event in column i
  type EventItem = (typeof items)[number];
  const assignments: { item: EventItem; col: number; totalCols: number }[] = [];

  // Group overlapping events
  const groups: EventItem[][] = [];
  let currentGroup: EventItem[] = [];
  let groupEnd = 0;

  for (const item of items) {
    if (currentGroup.length === 0 || item.startMinutes < groupEnd) {
      // Overlaps with current group
      currentGroup.push(item);
      groupEnd = Math.max(groupEnd, item.endMinutes);
    } else {
      // Start a new group
      if (currentGroup.length > 0) groups.push(currentGroup);
      currentGroup = [item];
      groupEnd = item.endMinutes;
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);

  // Within each group, assign columns
  for (const group of groups) {
    const groupColumns: number[] = []; // endMinutes per column within this group

    for (const item of group) {
      // Find the first column where this event fits
      let col = groupColumns.findIndex((endMin) => endMin <= item.startMinutes);
      if (col === -1) {
        col = groupColumns.length;
        groupColumns.push(0);
      }
      groupColumns[col] = item.endMinutes;
      assignments.push({ item, col, totalCols: 0 }); // totalCols set below
    }

    // Set totalCols for all items in this group
    const totalCols = groupColumns.length;
    for (const a of assignments) {
      if (group.some((g) => g.event.id === a.item.event.id)) {
        a.totalCols = totalCols;
      }
    }
  }

  // Convert to pixel positions
  return assignments.map(({ item, col, totalCols }) => ({
    event: item.event,
    top: item.startMinutes * pxPerMinute,
    height: Math.max((item.endMinutes - item.startMinutes) * pxPerMinute, MIN_HEIGHT),
    leftPercent: (col / totalCols) * 100,
    widthPercent: (1 / totalCols) * 100,
    startMinutes: item.startMinutes,
    endMinutes: item.endMinutes,
  }));
}

/**
 * Group events by date key, handling multi-day events.
 */
export function groupEventsByDate(events: CalendarEvent[]): Record<string, CalendarEvent[]> {
  const map: Record<string, CalendarEvent[]> = {};
  for (const e of events) {
    const start = new Date(e.start_date + "T00:00:00");
    const end = e.end_date ? new Date(e.end_date + "T00:00:00") : start;
    const cur = new Date(start);
    while (cur <= end) {
      const k = dateKey(cur);
      if (!map[k]) map[k] = [];
      map[k].push(e);
      cur.setDate(cur.getDate() + 1);
    }
  }
  return map;
}
